import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { RestaurantHeader } from '../components/restaurant/RestaurantHeader';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData, useMarketplaceData } from '../lib/hooks';
import { useCart } from '../lib/cart';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Store, Utensils, ChevronRight } from 'lucide-react';
import { MenuItem, CartItemOption } from '../lib/types';
export function RestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    settings,
    loading: restaurantLoading,
    error
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    zones
  } = useMarketplaceData();
  const {
    addItem
  } = useCart();
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options
    }, restaurant.name);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  if (restaurantLoading) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1472">
        <MarketplaceHeader data-id="element-1473" />
        <Skeleton className="h-64 w-full rounded-none" data-id="element-1474" />
        <div className="max-w-5xl mx-auto px-4 w-full mt-8" data-id="element-1475">
          <Skeleton className="h-12 w-1/3 mb-4" data-id="element-1476" />
          <Skeleton className="h-4 w-2/3 mb-8" data-id="element-1477" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1478">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-2xl" data-id="element-1479" />)}
          </div>
        </div>
      </div>;
  }
  if (error || !restaurant) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1480">
        <MarketplaceHeader data-id="element-1481" />
        <div className="flex-1 flex items-center justify-center" data-id="element-1482">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible en este momento." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-1483" />
        </div>
        <Footer data-id="element-1484" />
      </div>;
  }
  const zoneName = zones.find(z => z.id === restaurant.zone_id)?.name;
  // Show first few items as highlights (is_featured not in menu_items schema)
  const highlightedItems = items.slice(0, 4);
  const primaryColor = restaurant.primary_color || '#F59E0B';
  return <div className="min-h-screen flex flex-col bg-brand-offwhite pb-24" data-id="element-1485">
      <MarketplaceHeader data-id="element-1486" />

      <main className="flex-1" data-id="element-1487">
        <RestaurantHeader restaurant={restaurant} settings={settings} zoneName={zoneName} data-id="element-1488" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1489">
          {/* Highlighted Items */}
          {highlightedItems.length > 0 && <section className="mb-12" data-id="element-1490">
              <div className="flex items-center justify-between mb-6" data-id="element-1491">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-1492">Destacados</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1493">
                {highlightedItems.map(item => <MenuItemCard key={item.id} item={item} onClick={handleProductClick} primaryColor={primaryColor} data-id="element-1494" />)}
              </div>
            </section>}

          {/* Categories Quick Nav */}
          <section className="mb-12" data-id="element-1495">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1496">
              Categorías
            </h2>
            {menuLoading ? <div className="flex gap-4 overflow-x-auto" data-id="element-1497">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 w-32 rounded-2xl shrink-0" data-id="element-1498" />)}
              </div> : categories.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-1499">
                {categories.map(category => <Link key={category.id} to={`/r/${restaurant.slug}/menu#${category.id}`} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all text-center group" data-id="element-1500">
                    <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 bg-gray-50 group-hover:scale-110 transition-transform" style={{
                color: primaryColor
              }} data-id="element-1501">
                      <Utensils className="w-6 h-6" data-id="element-1502" />
                    </div>
                    <h3 className="font-semibold text-gray-900" data-id="element-1503">
                      {category.name}
                    </h3>
                  </Link>)}
              </div> : <p className="text-gray-500" data-id="element-1504">No hay categorías disponibles.</p>}
          </section>

          {/* CTA Full Menu */}
          <div className="text-center bg-white p-8 rounded-2xl border border-gray-100 shadow-sm" data-id="element-1505">
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1506">
              ¿Listo para ordenar?
            </h3>
            <p className="text-gray-600 mb-2" data-id="element-1507">
              Explora el menú completo de{' '}
              <span className="font-semibold" data-id="element-1508">{restaurant.name}</span> y
              personaliza tu pedido.
            </p>
            <p className="text-xs text-gray-400 mb-6" data-id="element-1509">
              Tu pedido irá directamente a este restaurante por WhatsApp.
            </p>
            <Button size="lg" onClick={() => navigate(`/r/${restaurant.slug}/menu`)} style={{
            backgroundColor: primaryColor
          }} className="text-white hover:opacity-90 transition-opacity" data-id="element-1510">
              Ver Menú Completo <ChevronRight className="w-5 h-5 ml-2" data-id="element-1511" />
            </Button>
          </div>
        </div>
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-1512" />

      <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-1513" />

      <Footer data-id="element-1514" />
    </div>;
}